import { useEffect, useRef, useState } from "react";
import { io as defaultIo } from "socket.io-client";
import { createRealtimeClient } from "./client";
import { startRealtimeLifecycle } from "./lifecycle";
import { createMlStatusRepository } from "./mlStatusRepository";
import { onNetworkOnline } from "./netGuard";

export const useRealtimeClient = ({
  authToken,
  url,
  ioFactory,
  repositoryFactory,
  onError,
  entitlementRepositoryRef,
}) => {
  const resolvedIoFactory = ioFactory || defaultIo;
  const resolvedRepositoryFactory =
    repositoryFactory || createMlStatusRepository;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const [owner, setOwner] = useState(null);

  useEffect(() => {
    if (!authToken) {
      setOwner(null);
      return undefined;
    }
    const reportError = (...args) => onErrorRef.current?.(...args);
    const repository = resolvedRepositoryFactory({ onError: reportError });
    const client = createRealtimeClient({
      url,
      token: authToken,
      ioFactory: resolvedIoFactory,
      onReconnect: () => {
        repository.resync("reconnect");
        entitlementRepositoryRef?.current?.refresh("reconnect");
      },
      onDisconnect: (reason) =>
        entitlementRepositoryRef?.current?.markDisconnected(reason),
      onError: reportError,
    });
    const cleanup = startRealtimeLifecycle({ client, repository });
    const detachOnlineWatch = onNetworkOnline(() => {
      client.forceReconnect();
      entitlementRepositoryRef?.current?.refresh("network-online");
    });
    setOwner({ token: authToken, client, repository });
    return () => {
      detachOnlineWatch();
      cleanup();
    };
  }, [authToken, url, resolvedIoFactory, resolvedRepositoryFactory]);

  return owner;
};

export default useRealtimeClient;
